/**
 * The d-pad and A on any screen made of buttons.
 *
 * Every panel in the game is DOM: the board, the grade sheet, the summary, the
 * chapter card, the hiring screen, the title and the pause menu. Each was built
 * for a mouse and a few keys, and none of them should have to learn about a
 * pad. So this does what a console does to a web page: it keeps one focused
 * control on whatever screen is on top, moves it with the d-pad (or the left
 * stick, past a firm push, repeating when held) to the nearest control in that
 * direction, presses it with A, nudges a slider with left and right, scrolls
 * the panel with the right stick (and with the d-pad when there is nothing
 * further to move to), and hands B to whoever owns the screen.
 *
 * It only ever clicks. Anything a pad can do on a panel is something a mouse
 * could already do there, through the same listener, so no panel has a second
 * way of being used that could drift from the first.
 */

import { SCROLL_SPEED as SCROLL } from '../core/gamepad.js';

const FOCUSABLE = 'button:not([disabled]), input[type="range"], input[type="checkbox"], [data-pad]';

function visible(node) {
  if (!node || node.hidden) return false;
  if (node.closest('[hidden]')) return false;
  const box = node.getBoundingClientRect();
  return box.width > 0 && box.height > 0;
}

function centre(node) {
  const box = node.getBoundingClientRect();
  return { x: box.left + box.width / 2, y: box.top + box.height / 2 };
}

/**
 * The nearest control in a direction, by a console's rule: mostly along the
 * direction, a little across it. Falls back to list order when nothing lies
 * that way, so up and down always go somewhere on a single column.
 */
export function nearest(from, candidates, direction) {
  const axis = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] }[direction];
  let best = null;
  let bestScore = Infinity;
  for (const candidate of candidates) {
    if (candidate.node === from.node) continue;
    const dx = candidate.x - from.x;
    const dy = candidate.y - from.y;
    const along = dx * axis[0] + dy * axis[1];
    if (along <= 1) continue;
    const across = Math.abs(dx * axis[1] - dy * axis[0]);
    const score = along + across * 2.5;
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return best;
}

/**
 * What scrolls on a panel: the panel itself if it scrolls, otherwise the first
 * thing inside it that has more to show than it has room for.
 */
export function scrollerOf(root) {
  if (!root) return null;
  const scrolls = (node) => {
    if (!node || node.scrollHeight <= node.clientHeight + 2) return false;
    const style = globalThis.getComputedStyle?.(node);
    return !style || /(auto|scroll)/.test(style.overflowY);
  };
  if (scrolls(root)) return root;
  for (const node of root.querySelectorAll('*')) if (scrolls(node)) return node;
  return null;
}

/** Pixels a d-pad press scrolls when there is no control further that way. */
const STEP = 140;

export function createPadNav() {
  let focused = null;
  let scope = null;

  function controls(root) {
    return [...root.querySelectorAll(FOCUSABLE)].filter(visible);
  }

  function mark(node) {
    if (focused === node) return;
    focused?.classList.remove('pad-focus');
    focused = node;
    if (focused) {
      focused.classList.add('pad-focus');
      focused.scrollIntoView?.({ block: 'nearest' });
    }
  }

  function move(direction, list, root) {
    const scroller = scrollerOf(root);
    if (!focused || !list.includes(focused)) {
      if (list.length) mark(list[0]);
      else if (scroller && (direction === 'up' || direction === 'down')) scroller.scrollBy?.({ top: direction === 'down' ? STEP : -STEP });
      return;
    }
    const here = { node: focused, ...centre(focused) };
    const next = nearest(here, list.map((node) => ({ node, ...centre(node) })), direction);
    if (next) {
      mark(next.node);
      return;
    }
    /*
     * Nothing further that way. A panel with more to read below or above
     * scrolls first (a summary with one button at the bottom is read with the
     * d-pad, not skipped), and a single column wraps round only once there is
     * nothing left to scroll.
     */
    if (scroller && (direction === 'up' || direction === 'down')) {
      const before = scroller.scrollTop;
      scroller.scrollBy?.({ top: direction === 'down' ? STEP : -STEP });
      if (scroller.scrollTop !== before) return;
    }
    const at = list.indexOf(focused);
    if (direction === 'down') mark(list[(at + 1) % list.length]);
    if (direction === 'up') mark(list[(at - 1 + list.length) % list.length]);
  }

  function nudge(node, by) {
    if (node?.type !== 'range') return false;
    const step = Number(node.step) || 0.05;
    const lo = Number(node.min);
    const hi = Number(node.max);
    node.value = String(Math.min(hi, Math.max(lo, Number(node.value) + step * by)));
    node.dispatchEvent(new Event('input', { bubbles: true }));
    node.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  return {
    get focused() {
      return focused;
    },
    /**
     * One frame on a screen. `root` is the screen on top (or null for none),
     * `pad` is the gamepad layer with its edges, `onBack` is what B means here.
     */
    step(root, pad, { onBack = null } = {}) {
      if (root !== scope) {
        scope = root;
        mark(null);
      }
      if (!root) return;
      const list = controls(root);
      if (focused && !list.includes(focused)) mark(null);
      if (!focused && list.length && pad.state.active) mark(list.find((n) => n.dataset.padDefault != null) ?? list[0]);
      // A push and then its held repeat, from the d-pad or the left stick.
      const fires = (direction) => (pad.nav ? pad.nav(direction) : pad[direction]());
      if (fires('up')) move('up', list, root);
      if (fires('down')) move('down', list, root);
      if (fires('left') && !nudge(focused, -1)) move('left', list, root);
      if (fires('right') && !nudge(focused, 1)) move('right', list, root);
      // The right stick scrolls whatever the panel scrolls.
      const scroll = pad.state?.scroll;
      if (scroll && Math.abs(scroll.y) > 0) scrollerOf(root)?.scrollBy?.({ top: scroll.y * SCROLL * (1 / 60) });
      if (pad.confirm() && focused) {
        if (focused.type === 'checkbox') {
          focused.checked = !focused.checked;
          focused.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (focused.type !== 'range') {
          focused.click();
        }
      }
      if (pad.cancel()) onBack?.();
    },
    clear() {
      mark(null);
      scope = null;
    },
  };
}
